import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Bell, Trash2 } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';

const FREQ_LABELS = {
  instant: 'Instant',
  daily: 'Daily digest',
  weekly: 'Weekly digest',
};

/**
 * /alerts — list of the student's saved searches with per-alert controls.
 * A minimal functional page; the rich "Save this search from /search" modal
 * is Phase B. Empty state directs users to /search to create their first alert.
 */
const Alerts = () => {
  const { api } = useAuth();
  const { refetch } = useProfile();
  const navigate = useNavigate();
  const [searches, setSearches] = useState(null);

  const load = async () => {
    const r = await api.get('/saved-searches');
    setSearches(r.data.saved_searches);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAlert = async (s) => {
    const res = await api.patch(`/saved-searches/${s.saved_search_id}`, {
      alert_enabled: !s.alert_enabled,
    });
    setSearches((prev) => prev.map((x) => (x.saved_search_id === s.saved_search_id ? res.data : x)));
    toast.success(res.data.alert_enabled ? 'Alerts on.' : 'Alerts paused.');
  };

  const changeFrequency = async (s, freq) => {
    const res = await api.patch(`/saved-searches/${s.saved_search_id}`, {
      alert_frequency: freq,
    });
    setSearches((prev) => prev.map((x) => (x.saved_search_id === s.saved_search_id ? res.data : x)));
  };

  const remove = async (s) => {
    await api.delete(`/saved-searches/${s.saved_search_id}`);
    setSearches((prev) => prev.filter((x) => x.saved_search_id !== s.saved_search_id));
    refetch();
    toast.success('Alert removed.');
  };

  return (
    <div className="min-h-screen bg-background pb-12" data-testid="alerts-page">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-5 py-6 space-y-5">
        <div>
          <h1 className="text-2xl font-heading font-extrabold text-slate-900">Your alerts</h1>
          <p className="mt-1 text-sm text-slate-600">
            We email you when a new home matches your filters. No spam.
          </p>
        </div>

        {searches === null ? (
          <div className="py-10 text-center">
            <Loader2 className="h-5 w-5 animate-spin text-amber-600 mx-auto" />
          </div>
        ) : searches.length === 0 ? (
          <Card className="bg-white border-slate-200/70 shadow-soft" data-testid="alerts-empty">
            <CardContent className="p-6 text-center">
              <Bell className="h-8 w-8 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-600 mb-4">
                You don't have any alerts yet. Create one from the search page.
              </p>
              <Button
                className="btn-primary px-5 py-5 text-sm"
                onClick={() => navigate('/search')}
                data-testid="alerts-create-btn"
              >
                Go to search
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="alerts-list">
            {searches.map((s) => (
              <Card key={s.saved_search_id} className="bg-white border-slate-200/70 shadow-soft">
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-semibold text-sm text-slate-900 truncate">
                        {s.name}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Created {new Date(s.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Switch
                      checked={!!s.alert_enabled}
                      onCheckedChange={() => toggleAlert(s)}
                      data-testid={`alert-toggle-${s.saved_search_id}`}
                    />
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <Select
                      value={s.alert_frequency}
                      onValueChange={(v) => changeFrequency(s, v)}
                    >
                      <SelectTrigger
                        className="h-10 bg-white w-44 text-xs"
                        data-testid={`alert-freq-${s.saved_search_id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(FREQ_LABELS).map(([v, l]) => (
                          <SelectItem key={v} value={v}>
                            {l}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(s)}
                      className="text-red-600 hover:bg-red-50 ml-auto"
                      data-testid={`alert-delete-${s.saved_search_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Alerts;
